import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { useIntl } from '../../util/reactIntl';
import { isErrorNoViewingPermission, isErrorUserPendingApproval } from '../../util/errors';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import {
  needsAccountRoleChoice,
  needsEmailVerification,
  shouldRedirectToListeningProfileSetup,
} from '../../util/accountRole';

import { NamedRedirect } from '../../components';

/**
 * Soft browse: a private-marketplace 403 must keep guests on /s (Find a
 * Listener), never dump them on the Sign up form.
 *
 * @param {Object} [params]
 * @returns {boolean}
 */
export const shouldRedirectSearchToSignup = () => false;

/**
 * Search is Talk browse. Unverified and unpaid-role accounts must not land
 * here. Listen-only without a profile goes to Dashboard (finish-setup banner),
 * never Listening profile Details.
 *
 * @param {Object} params
 * @param {Object} [params.currentUser]
 * @param {boolean} [params.currentUserHasListings]
 * @returns {{ name: string, search?: string }|null}
 */
export const searchAccessRedirect = ({
  currentUser,
  currentUserHasListings = false,
  currentUserOwnListing = null,
  currentUserHasListingsFetched = false,
} = {}) => {
  if (needsEmailVerification(currentUser, { currentUserHasListings })) {
    return { name: 'EmailVerificationPage' };
  }
  if (needsAccountRoleChoice(currentUser, { currentUserHasListings })) {
    return { name: 'ChooseAccountRolePage' };
  }
  if (
    shouldRedirectToListeningProfileSetup(currentUser, {
      currentUserHasListings,
      currentUserOwnListing,
      currentUserHasListingsFetched,
    })
  ) {
    return { name: 'DashboardPage', search: '?tab=listening' };
  }
  return null;
};

/**
 * @param {Object} [params]
 * @returns {string|null} route name to redirect to
 */
export const searchAccessRedirectName = params => searchAccessRedirect(params)?.name || null;

/**
 * Shared access gating for SearchPage variants: forbidden, private marketplace,
 * pending approval, and no viewing rights. Injects router/context props into the page component.
 *
 * @param {Object} props
 * @param {React.ComponentType} props.PageComponent - Search page class receiving injected props
 * @param {Object} props - Remaining props from Redux `connect`
 * @returns {JSX.Element}
 */
const SearchPageAccessWrapper = ({ PageComponent, ...rest }) => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  React.useEffect(() => {
    try { window.sessionStorage.setItem('sl_last_browse_search', location.search || ''); } catch (e) {}
  }, [location.search]);

  const {
    currentUser,
    isAuthenticated = false,
    currentUserHasListings = false,
    currentUserOwnListing = null,
    currentUserHasListingsFetched = false,
    searchListingsError,
    ...restOfProps
  } = rest;
  const postAuthRedirect = searchAccessRedirect({
    currentUser,
    currentUserHasListings,
    currentUserOwnListing,
    currentUserHasListingsFetched,
  });
  if (postAuthRedirect) {
    return <NamedRedirect name={postAuthRedirect.name} search={postAuthRedirect.search} />;
  }
  if (shouldRedirectSearchToSignup({ searchListingsError, currentUser, isAuthenticated })) {
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(searchListingsError);
  const hasNoViewingRightsError = isErrorNoViewingPermission(searchListingsError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  }
  if (hasNoViewingRightsUser || hasNoViewingRightsError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <PageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      currentUser={currentUser}
      currentUserHasListings={currentUserHasListings}
      searchListingsError={searchListingsError}
      {...restOfProps}
    />
  );
};

export default SearchPageAccessWrapper;
